import { FunctionComponent, useEffect, useState, useContext } from "react";
import Paper from '@mui/material/Paper';
import { Box, Container, Typography, Button, Collapse } from "@mui/material";
import ParallaxSection from "./ParallaxSection";
import { styled } from '@mui/material/styles';
import Announcements from "./announcements/Announcements";
import { useNavigate } from "react-router-dom";
import { useAuth, UserContextType } from "./auth";
import PrayerRequestModal from "./components/PrayerRequestModal";
import { OverlayContainer, TitleBox, VerseContainer } from "./ParallaxSectionStyle";

const BIBLE_VERSES = [
  { text: "For I know the plans I have for you,\" declares the Lord, \"plans to prosper you and not to harm you, plans to give you hope and a future.", reference: "Jeremiah 29:11" },
  { text: "But those who hope in the Lord will renew their strength. They will soar on wings like eagles; they will run and not grow weary, they will walk and not be faint.", reference: "Isaiah 40:31" },
  { text: "I can do all this through him who gives me strength.", reference: "Philippians 4:13" },
  { text: "Trust in the Lord with all your heart and lean not on your own understanding; in all your ways submit to him, and he will make your paths straight.", reference: "Proverbs 3:5-6" },
  { text: "And we know that in all things God works for the good of those who love him, who have been called according to his purpose.", reference: "Romans 8:28" },
  { text: "The Lord is my shepherd, I lack nothing. He makes me lie down in green pastures, he leads me beside quiet waters, he refreshes my soul.", reference: "Psalm 23:1-3" },
  { text: "Be strong and courageous. Do not be afraid; do not be discouraged, for the Lord your God will be with you wherever you go.", reference: "Joshua 1:9" },
  { text: "But seek first his kingdom and his righteousness, and all these things will be given to you as well.", reference: "Matthew 6:33" }
];

const ContentSection = styled('section')(({ theme }) => ({
  padding: theme.spacing(4, 0),
  backgroundColor: 'white',
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.grey[50],
  },
}));

const SectionContent = styled(Container)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const AnnouncementPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  width: '100%',
  maxWidth: '800px',
  margin: '0 auto',
  backgroundColor: 'white',
  boxShadow: theme.shadows[2],
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
  },
}));

const AnimatedContainer = styled(Box)(({ theme }) => ({
  transition: 'all 0.3s ease-in-out',
  overflow: 'hidden',
}));

const VisitorSection = styled('section')(({ theme }) => ({
  padding: theme.spacing(6, 0),
  backgroundImage: `
    linear-gradient(150deg, ${theme.palette.primary.light} 25%, transparent 25%),
    linear-gradient(30deg, ${theme.palette.primary.light} 25%, transparent 25%),
    linear-gradient(150deg, transparent 75%, ${theme.palette.primary.light} 75%),
    linear-gradient(30deg, transparent 75%, ${theme.palette.primary.light} 75%)
  `,
  backgroundSize: '40px 70px',
  backgroundPosition: '0 0, 20px 0, 20px 35px, 0 35px',
  backgroundColor: theme.palette.background.default,
}));

const VisitorCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '600px',
  margin: '0 auto',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  boxShadow: theme.shadows[4],
  textAlign: 'center',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
  },
}));

const PrayerRequestSection = styled('section')(({ theme }) => ({
  padding: theme.spacing(6, 0),
  backgroundColor: theme.palette.background.paper,
}));

const PrayerRequestCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '600px',
  margin: '0 auto',
  backgroundColor: 'white',
  boxShadow: theme.shadows[4],
  textAlign: 'center',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
  },
}));

const Home: FunctionComponent = () => {
  const [showAnnouncements, setShowAnnouncements] = useState(false);
  const [showPrayerModal, setShowPrayerModal] = useState(false);
  const navigate = useNavigate();
  const auth = useContext<UserContextType>(useAuth);

  const [randomVerse] = useState(() =>
    BIBLE_VERSES[Math.floor(Math.random() * BIBLE_VERSES.length)]
  );

  const handlePrayerRequestClick = () => {
    if (auth?.IsAuthenticated) {
      setShowPrayerModal(true);
    } else {
      navigate('/login');
    }
  };

  return (
    <>
      <ParallaxSection videoSrc="https://cogcincinnati.s3.amazonaws.com/band.mp4" type="hero">
        <OverlayContainer>
          <TitleBox>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: 'primary.main', mb: 0 }}>
              Church of God Cincinnati
            </Typography>
          </TitleBox>
          <VerseContainer>
            <Typography variant="h5" sx={{ fontStyle: 'italic', mb: 1 }}>
              "{randomVerse.text}"
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, opacity: 0.9 }}>
              — {randomVerse.reference}
            </Typography>
          </VerseContainer>
        </OverlayContainer>
      </ParallaxSection>

      <ContentSection id="welcome">
        <SectionContent maxWidth="lg">
          <Typography variant="h2" component="h1" gutterBottom>
            Welcome to Church of God Cincinnati
          </Typography>
          <Typography variant="h5" color="text.secondary" align="center">
            Join us in worship and fellowship
          </Typography>
        </SectionContent>
      </ContentSection>

      <ContentSection id="announcements">
        <SectionContent maxWidth="lg">
          <Typography variant="h3" component="h2" gutterBottom>
            Announcements
          </Typography>
          <AnnouncementPaper elevation={2}>
            <Typography variant="h5" gutterBottom>Latest Updates</Typography>
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setShowAnnouncements(!showAnnouncements)}
                sx={{
                  transition: 'all 0.3s ease-in-out',
                  transform: showAnnouncements ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              >
                {showAnnouncements ? 'Hide Announcements' : 'See All Announcements'}
              </Button>
            </Box>
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <a
                href="/api/printable/bulletin"
                target="_blank"
                style={{ fontSize: '0.8rem', color: '#888', textDecoration: 'none' }}
              >
                Printable Bulletin
              </a>
            </Box>
            <Collapse in={showAnnouncements} timeout={500}>
              <AnimatedContainer>
                <Announcements />
              </AnimatedContainer>
            </Collapse>
          </AnnouncementPaper>
        </SectionContent>
      </ContentSection>

      <VisitorSection id="visitors">
        <SectionContent maxWidth="lg">
          <VisitorCard elevation={3}>
            <Typography variant="h3" component="h2" gutterBottom>
              First Time Visitors
            </Typography>
            <Typography variant="h6" color="text.secondary" paragraph>
              We're excited to welcome you to our church family
            </Typography>
            <Typography variant="body1" paragraph>
              Whether you're new to the area or just visiting, we'd love to get to know you better.
              Find out more about our services, ministries, and what to expect when you visit.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/visitors')}
              sx={{
                mt: 3,
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                textTransform: 'none',
              }}
            >
              Learn More About Visiting
            </Button>
          </VisitorCard>
        </SectionContent>
      </VisitorSection>

      <PrayerRequestSection id="prayer-requests">
        <SectionContent maxWidth="lg">
          <PrayerRequestCard elevation={3}>
            <Typography variant="h3" component="h2" gutterBottom>
              Prayer Requests
            </Typography>
            <Typography variant="h6" color="text.secondary" paragraph>
              Share your prayer needs with our community
            </Typography>
            <Typography variant="body1" paragraph>
              We believe in the power of prayer. Submit your prayer requests and let our community join you in prayer.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handlePrayerRequestClick}
              sx={{
                mt: 3,
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                textTransform: 'none',
              }}
            >
              Submit Prayer Request
            </Button>
          </PrayerRequestCard>
        </SectionContent>
      </PrayerRequestSection>

      <PrayerRequestModal
        open={showPrayerModal}
        onClose={() => setShowPrayerModal(false)}
      />
    </>
  );
};

export default Home;
